import { useState } from 'react';

import CustomModal from '@/components/CustomModal';
import { RadioButtonGroup } from '@/components/RadioButton';
import Select from '@/components/Select';
import { STATUS_OPTIONS, DEVICE_USER_TYPE_OPTIONS } from '@/constants/constant';
import { ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import { IDeviceFilterValues } from '@/types';

const DeviceFilterModal = ({
  setIsOpen,
  onApply,
  initialValues = {},
  deviceModelOptions,
}: {
  setIsOpen: (open: boolean) => void;
  onApply: (filters: IDeviceFilterValues) => void;
  initialValues?: Partial<IDeviceFilterValues>;
  deviceModelOptions: { value: string; label: string }[];
}) => {
  const { hasAccess } = useAccess();

  const { formatText } = useFormatText();
  const [status, setStatus] = useState(initialValues.status || '');
  const [role, setRole] = useState(initialValues.role || '');
  const [deviceType, setDeviceType] = useState(initialValues.device_type || '');

  const handleClear = () => {
    setStatus('');
    setRole('');
    setDeviceType('');
    onApply({ status: '', role: '', device_type: '' });
    setIsOpen(false);
  };

  const handleApply = () => {
    const filters = { status, role, device_type: deviceType };
    onApply(filters);
    setIsOpen(false);
  };

  if (!hasAccess(ERoleAccess.DEVICE_MANAGEMENT)) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      isFooter
      heading={formatText('common.actions.filter')}
      position="right"
      className="max-w-150"
      confirmButtonProps={{
        label: formatText('common.actions.show-results'),
        variant: 'primary',
        onClick: handleApply,
        disabled: !status && role.length === 0 && deviceType.length === 0,
      }}
      cancelButtonProps={{
        label: formatText('common.actions.clear-filters'),
        desructive: true,
        onClick: handleClear,
        className: 'text-sm underline px-0!',
      }}
    >
      <div className="flex flex-col gap-6 w-full">
        {/* Roles Multi-Select */}
        <Select
          name={formatText('device-management.filter.users')}
          options={DEVICE_USER_TYPE_OPTIONS}
          selected={role}
          onChange={(value) => setRole(value as string)}
          placeholder={formatText('device-management.filter.select-users')}
          isClearable={true}
          isTextFormat={true}
          className="min-h-13"
        />
        <Select
          name={formatText('device-management.filter.device-type')}
          options={deviceModelOptions}
          selected={deviceType}
          onChange={(value) => setDeviceType(value as string)}
          placeholder={formatText(
            'device-management.filter.select-device-type'
          )}
          isClearable={true}
          isTextFormat={true}
          className="min-h-13"
        />

        {/* Status */}
        <RadioButtonGroup
          name="status"
          label={formatText('device-management.filter.status')}
          options={STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
          formatLabel={formatText}
        />
      </div>
    </CustomModal>
  );
};

export default DeviceFilterModal;
